// pages/public-grants.ts — recent grants ledger.
//
// First page in the migration that actually talks to the backend.
// Demonstrates the render() + mount() pattern: render() emits the static
// frame (hero, summary skeleton, loading row, CTA); mount() awaits
// api.publicGrants() and then mutates the DOM in place.
//
// vw_PublicGrants row shape (mirrored from frontend2/Public.jsx):
//   { ProjectName, OrganizationLegalName, ApprovedAmount, ApprovedYear,
//     Description, IsAmountPublic }
// IsAmountPublic=false masks the dollar amount in both the summary
// strip and the row.

import type { Page, PageContext } from '../router';
import { html, esc, arrowR, fmtMoney } from '../util';

interface Row {
  ProjectName: string;
  OrganizationLegalName: string;
  ApprovedAmount: number;
  ApprovedYear: number;
  Description?: string;
  IsAmountPublic: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────
function uniqueYears(rows: Row[]): string[] {
  const set = new Set<string>();
  for (const r of rows) if (r.ApprovedYear) set.add(String(r.ApprovedYear));
  return ['All', ...Array.from(set).sort((a, b) => Number(b) - Number(a))];
}

function summary(rows: Row[], year: string): { total: number; count: number; avg: number } {
  let total = 0, count = 0;
  for (const r of rows) {
    if (r.IsAmountPublic) {
      total += Number(r.ApprovedAmount || 0);
      count += 1;
    }
  }
  void year;
  return { total, count, avg: count > 0 ? Math.round(total / count) : 0 };
}

function renderYearTabs(years: string[], active: string): string {
  if (years.length <= 1) return '';
  return html`
    <div class="d-flex card bff-year-tabs" style="padding: 6px; gap: 4px;">
      ${{ __raw: years.map(y => `
        <button class="bff-year-tab${y === active ? ' is-active' : ''}" data-year="${esc(y)}">
          ${esc(y)}
        </button>
      `).join('') }}
    </div>
  `;
}

function renderSummary(rows: Row[], year: string): string {
  const { total, count, avg } = summary(rows, year);
  const totalLabel = year === 'All' ? 'awarded (public amounts)' : `awarded in ${esc(year)} (public)`;
  return html`
    <div>
      <div class="serif" style="font-size: 44px; color: var(--primary); line-height: 1;">${rows.length}</div>
      <div class="t-small" style="margin-top: 8px;">grants shown</div>
    </div>
    <div>
      <div class="serif" style="font-size: 44px; color: var(--primary); line-height: 1;">${fmtMoney(total)}</div>
      <div class="t-small" style="margin-top: 8px;">${{ __raw: totalLabel }}</div>
    </div>
    <div>
      <div class="serif" style="font-size: 44px; color: var(--primary); line-height: 1;">${count > 0 ? fmtMoney(avg) : '—'}</div>
      <div class="t-small" style="margin-top: 8px;">average grant</div>
    </div>
  `;
}

function renderLedger(rows: Row[]): string {
  if (rows.length === 0) {
    return '<p class="t-body">No public grants yet.</p>';
  }
  const body = rows.map(g => html`
    <tr>
      <td class="num">${g.ApprovedYear || '—'}</td>
      <td style="padding-right: 32px;">
        <div class="serif" style="font-size: 20px; line-height: 1.25; margin-bottom: 6px;">${g.ProjectName}</div>
        ${{ __raw: g.Description
          ? html`<p class="t-body" style="margin: 0; font-size: 14px; max-width: 560px; color: var(--ink-soft);">${g.Description}</p>`
          : '' }}
      </td>
      <td><span style="color: var(--primary);">${g.OrganizationLegalName}</span></td>
      <td><span class="pill pill-approved">Approved</span></td>
      <td style="text-align: right;">
        ${{ __raw: g.IsAmountPublic
          ? html`<span class="serif" style="font-size: 22px; color: var(--primary);">${fmtMoney(g.ApprovedAmount)}</span>`
          : html`<span class="t-small" style="color: var(--ink-mute);">Amount not disclosed</span>` }}
      </td>
    </tr>
  `).join('');

  return html`
    <table class="ledger">
      <thead>
        <tr>
          <th style="width: 80px;">Year</th>
          <th>Project</th>
          <th>Organization</th>
          <th style="width: 140px;">Status</th>
          <th style="width: 160px; text-align: right;">Amount</th>
        </tr>
      </thead>
      <tbody>${{ __raw: body }}</tbody>
    </table>
  `;
}

// ─── Page ────────────────────────────────────────────────────────
const PublicGrantsPage: Page = {
  render(_ctx: PageContext): string {
    return html`
      <section style="padding: 80px 0 56px;">
        <div class="wrap">
          <div class="d-flex bff-pg-head" style="justify-content: space-between; align-items: end; gap: 32px; flex-wrap: wrap;">
            <div>
              <div class="eyebrow">Recent grants</div>
              <h1 class="t-h1" style="margin: 16px 0 0;">
                Some of the grants we've funded.
              </h1>
              <p class="t-lede" style="margin-top: 32px; max-width: 580px;">
                A glimpse of where recent grants have gone.
              </p>
            </div>
            <div id="pg-year-tabs"></div>
          </div>
          <div id="pg-summary" class="bff-pg-summary">
            <div class="t-small">Loading recent grants…</div>
          </div>
        </div>
      </section>

      <section style="padding: 32px 0 96px;">
        <div class="wrap">
          <div id="pg-ledger">
            <p class="t-body">Loading grants…</p>
          </div>
        </div>
      </section>

      <section class="divide divide-soft" style="padding: 80px 0;">
        <div class="wrap" style="text-align: center; max-width: 680px; margin: 0 auto;">
          <div class="eyebrow" style="display: inline-block;">Become a grantee</div>
          <h2 class="t-h2" style="margin: 16px 0 0;">Want to see your project on this list?</h2>
          <div class="d-flex" style="gap: 12px; margin-top: 32px; justify-content: center; flex-wrap: wrap;">
            <a class="btn btn-outline-primary" data-nav="grant-info">How to apply</a>
            <a class="btn btn-primary" data-nav="grant-info">Apply for a grant ${{ __raw: arrowR() }}</a>
          </div>
        </div>
      </section>
    `;
  },

  mount(root: HTMLElement, _ctx: PageContext): void {
    // Local state for the mount lifetime.
    let allRows: Row[] = [];
    let year = 'All';
    let alive = true;

    const tabsEl    = root.querySelector('#pg-year-tabs')   as HTMLElement | null;
    const summaryEl = root.querySelector('#pg-summary')     as HTMLElement | null;
    const ledgerEl  = root.querySelector('#pg-ledger')      as HTMLElement | null;

    function paint(): void {
      const filtered = year === 'All'
        ? allRows
        : allRows.filter(r => String(r.ApprovedYear) === year);

      if (tabsEl)    tabsEl.innerHTML    = renderYearTabs(uniqueYears(allRows), year);
      if (summaryEl) summaryEl.innerHTML = renderSummary(filtered, year);
      if (ledgerEl)  ledgerEl.innerHTML  = renderLedger(filtered);
    }

    function paintError(message: string): void {
      if (tabsEl)    tabsEl.innerHTML    = '';
      if (summaryEl) summaryEl.innerHTML = '';
      if (ledgerEl) {
        ledgerEl.innerHTML =
          '<p class="t-body" style="color: var(--st-denied);">' +
          'Could not load grants (' + esc(message) + '). Please try again later.' +
          '</p>';
      }
    }

    // Year-tab clicks are delegated from the page root.
    root.addEventListener('click', (e) => {
      const tab = (e.target as HTMLElement | null)?.closest('[data-year]') as HTMLElement | null;
      if (!tab) return;
      e.preventDefault();
      year = tab.getAttribute('data-year') || 'All';
      paint();
    });

    window.api.publicGrants()
      .then(rows => {
        if (!alive) return;
        allRows = Array.isArray(rows) ? (rows as Row[]) : [];
        paint();
      })
      .catch(err => {
        if (!alive) return;
        paintError((err && (err.message || err.toString())) || 'Network error');
      });

    // Stash a cleanup hook on the page object so unmount() can stop us.
    (PublicGrantsPage as Page & { _alive?: () => void })._alive = () => { alive = false; };
  },

  unmount(): void {
    const stop = (PublicGrantsPage as Page & { _alive?: () => void })._alive;
    if (stop) stop();
  },
};

export default PublicGrantsPage;
